import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { date, dateTime, isoDate, money } from '@/lib/format'
import { zeroReasonLabel } from '@/lib/commission'
import { useAsync } from '@/lib/useAsync'
import { Dialog } from '@/components/ui/dialog'
import { Field, Input, Select, Textarea } from '@/components/ui/form'
import { PageHeader } from '@/components/ui/page'
import { Badge, Button, Card, CardHeader, EmptyState, ErrorState, Loading } from '@/components/ui/primitives'
import { Table, Td, Th, Tr } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type {
  CommissionOverview,
  CommissionTotal,
  PayoutSummary,
  ResellerListItem,
  StatementDetail,
  StatementLine,
} from '@/types/api'

/**
 * Settlement: what partners have earned, and what has been remitted.
 *
 * <p>
 * A destination of its own rather than a tab inside each reseller, because settling is
 * done a period at a time across partners - a screen that made somebody pick a reseller
 * first would be asking them to guess which one has anything waiting.
 * </p>
 * <p>
 * Hidden outright from anybody without <code>reseller.commission.view</code>. Not shown
 * and refused: the reason is a partner's privacy rather than a division of labour, and a
 * Manager should not learn that a commission screen exists for a partner whose rates are
 * none of their business.
 * </p>
 */
export function Commission() {
  const { can } = useAuth()
  const navigate = useNavigate()
  const [building, setBuilding] = useState(false)

  const overview = useAsync<CommissionOverview>(
    (signal) => api.get<CommissionOverview>('/statements', undefined, signal),
    [],
  )

  if (overview.loading && !overview.data) return <Loading />
  if (overview.error) {
    return (
      <ErrorState
        title={overview.error.title}
        detail={overview.error.detail}
        onRetry={overview.reload}
      />
    )
  }

  const data = overview.data
  if (!data) return null

  return (
    <>
      <PageHeader
        title="Commission"
        hint="What partners have earned on payments customers have already made, and what has been remitted."
        actions={
          can('reseller.statement.issue') ? (
            <Button variant="primary" onClick={() => setBuilding(true)}>
              Build a statement
            </Button>
          ) : undefined
        }
      />

      <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {data.unstatemented.map((total) => (
          <Total
            key={`u-${total.currencyCode}`}
            label="Unstatemented"
            total={total}
            hint={`${total.count} accrual${total.count === 1 ? '' : 's'}`}
          />
        ))}
        {data.issuedUnpaid.map((total) => (
          <Total
            key={`i-${total.currencyCode}`}
            label="Issued, unpaid"
            total={total}
            hint={`${total.count} statement${total.count === 1 ? '' : 's'}`}
            tone="warn"
          />
        ))}
        {data.unstatemented.length === 0 && data.issuedUnpaid.length === 0 && (
          <Card className="px-5 py-4 sm:col-span-2">
            <p className="text-[13px] text-muted">
              Nothing waiting. Every accrual is on a statement, and every statement has been
              paid.
            </p>
          </Card>
        )}
      </div>

      <Card>
        <CardHeader
          title="Statements"
          hint="Currencies are grouped, never summed - there are no FX rates in this system."
        />
        {data.statements.length === 0 ? (
          <EmptyState
            title="No statements yet"
            hint="A statement claims every unstatemented accrual in a period. Nothing on one is a forecast: it is all commission on money the customer has already sent us."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Reference</Th>
                <Th>Partner</Th>
                <Th>Period</Th>
                <Th numeric>Lines</Th>
                <Th numeric>Amount</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {data.statements.map((row) => (
                <Tr key={row.statement.id} onClick={() => navigate(`/commission/${row.statement.id}`)}>
                  <Td className="font-mono text-[12px] text-accent">{row.statement.reference}</Td>
                  <Td>
                    {row.resellerName}
                    <span className="ml-2 text-[12px] text-faint">{row.countryIsoCode}</span>
                  </Td>
                  <Td>
                    {date(row.statement.periodStart)} &ndash; {date(row.statement.periodEnd)}
                  </Td>
                  <Td numeric>{row.statement.lineCount}</Td>
                  <Td numeric>{money(row.statement.totalAmount, row.statement.currencyCode)}</Td>
                  <Td>
                    <Badge tone={row.statement.status === 'Paid' ? 'positive' : 'accent'}>
                      {row.statement.status}
                    </Badge>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <BuildStatement
        open={building}
        onClose={() => setBuilding(false)}
        onDone={() => {
          setBuilding(false)
          overview.reload()
        }}
      />
    </>
  )
}

function Total({
  label,
  total,
  hint,
  tone,
}: {
  label: string
  total: CommissionTotal
  hint: string
  tone?: 'warn'
}) {
  return (
    <Card className="px-5 py-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
        {label} &middot; {total.currencyCode}
      </p>
      <p className={`tabular mt-1.5 text-[22px] font-semibold ${tone === 'warn' ? 'text-warn' : ''}`}>
        {money(total.amount, total.currencyCode)}
      </p>
      <p className="mt-1 text-[12px] text-muted">{hint}</p>
    </Card>
  )
}

function BuildStatement({
  open,
  onClose,
  onDone,
}: {
  open: boolean
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [resellerId, setResellerId] = useState('')
  const [periodStart, setPeriodStart] = useState('')
  const [periodEnd, setPeriodEnd] = useState(isoDate())
  const [saving, setSaving] = useState(false)

  const resellers = useAsync<{ items: ResellerListItem[] }>(
    (signal) =>
      api.get<{ items: ResellerListItem[] }>('/resellers', { pageSize: 200 }, signal),
    [],
  )

  const chosen = (resellers.data?.items ?? []).find((r) => r.id === resellerId)

  async function build() {
    if (!chosen) return
    setSaving(true)
    try {
      await api.post(`/resellers/${resellerId}/statements`, {
        countryId: chosen.countryId,
        periodStart,
        periodEnd,
      })
      toast.show('Statement issued', 'success')
      onDone()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not build the statement', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="Build a statement"
      description="Claims every unstatemented accrual whose payment landed in the period. An accrual carries one statement id for ever, so the same earning cannot appear on two periods."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={build}
            disabled={saving || !chosen || periodStart === '' || periodEnd === ''}
          >
            {saving ? 'Building...' : 'Build'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Partner" required>
          {(id) => (
            <Select id={id} value={resellerId} onChange={(e) => setResellerId(e.target.value)}>
              <option value="">Choose a partner</option>
              {(resellers.data?.items ?? []).map((reseller) => (
                <option key={reseller.id} value={reseller.id}>
                  {reseller.name} — {reseller.countryIsoCode}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Period from" required hint="By the date the money arrived, not the date it was typed in.">
            {(id) => (
              <Input
                id={id}
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            )}
          </Field>
          <Field label="Period to" required>
            {(id) => (
              <Input
                id={id}
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
            )}
          </Field>
        </div>
      </div>
    </Dialog>
  )
}

export function StatementPage() {
  const { id } = useParams()
  return id ? (
    <Statement statementId={id} />
  ) : (
    <ErrorState title="No statement" detail="That link is missing a statement id." />
  )
}

function Statement({ statementId }: { statementId: string }) {
  const { can } = useAuth()
  const navigate = useNavigate()
  const [paying, setPaying] = useState(false)

  const detail = useAsync<StatementDetail>(
    (signal) => api.get<StatementDetail>(`/statements/${statementId}`, undefined, signal),
    [statementId],
  )

  if (detail.loading && !detail.data) return <Loading />
  if (detail.error) {
    return <ErrorState title={detail.error.title} detail={detail.error.detail} onRetry={detail.reload} />
  }

  const data = detail.data
  if (!data) return null

  const { statement, lines } = data
  const symbol = statement.currencyCode

  return (
    <>
      <PageHeader
        eyebrow={statement.reference}
        title={`${date(statement.periodStart)} – ${date(statement.periodEnd)}`}
        hint={`Issued ${dateTime(statement.issuedAt)}`}
        back={{ label: 'Back to commission', onClick: () => navigate('/commission') }}
        actions={
          statement.status === 'Issued' && can('reseller.payout.record') ? (
            <Button variant="primary" onClick={() => setPaying(true)}>
              Record payout
            </Button>
          ) : undefined
        }
      />

      <Card className="mb-5">
        <CardHeader
          title="Working"
          hint="Every line resolves to a payment, on a named deal, at a rate frozen when that quotation was submitted."
          action={
            <div className="flex items-center gap-3">
              <Badge tone={statement.status === 'Paid' ? 'positive' : 'accent'}>
                {statement.status}
              </Badge>
              <span className="tabular text-[18px] font-semibold">
                {money(statement.totalAmount, symbol)}
              </span>
            </div>
          }
        />
        <Table>
          <thead>
            <tr>
              <Th>Payment received</Th>
              <Th>Deal</Th>
              <Th>Product</Th>
              <Th numeric>Payment (net)</Th>
              <Th numeric>Year</Th>
              <Th>Basis</Th>
              <Th numeric>Rate</Th>
              <Th numeric>Commission</Th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <Tr key={line.accrualId}>
                <Td>{date(line.accruedOn)}</Td>
                <Td>
                  <span className="font-mono text-[12px]">{line.quotationNo ?? '—'}</span>
                  {line.customerName && (
                    <span className="block text-[12px] text-muted">{line.customerName}</span>
                  )}
                  {line.soldByResellerName && (
                    // An override line. Naming the downline is what makes the figure
                    // explainable - without it the partner is looking at a deal they never sold.
                    <span className="block text-[12px] text-spark">
                      Override on {line.soldByResellerName}
                    </span>
                  )}
                </Td>
                <Td className="text-muted">
                  {line.productName ?? <span className="text-faint">&mdash;</span>}
                </Td>
                <Td numeric>{money(line.basisAmount, symbol)}</Td>
                <Td numeric>{line.contractYear}</Td>
                <Td>{line.billingKind === 'OneTime' ? 'One-time' : 'Recurring'}</Td>
                <Td numeric>
                  {line.zeroReason ? (
                    <span className="text-muted">&mdash;</span>
                  ) : (
                    `${line.percentApplied.toFixed(2)}%`
                  )}
                </Td>
                <Td numeric>
                  {line.zeroReason ? <ZeroReason line={line} /> : money(line.amount, symbol)}
                </Td>
              </Tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <Td className="font-medium" colSpan={7}>
                Total
              </Td>
              <Td numeric className="font-medium">
                {money(statement.totalAmount, symbol)}
              </Td>
            </tr>
          </tfoot>
        </Table>
      </Card>

      <Payouts statementId={statementId} />

      <RecordPayout
        open={paying}
        statementId={statementId}
        resellerId={statement.resellerId}
        amount={money(statement.totalAmount, symbol)}
        onClose={() => setPaying(false)}
        onDone={() => {
          setPaying(false)
          detail.reload()
        }}
      />
    </>
  )
}

/**
 * A zero, with the reason it is one.
 *
 * <p>
 * Written into the statement rather than omitted: an absent row is silence, and silence
 * is what generates the email to finance.
 * </p>
 */
function ZeroReason({ line }: { line: StatementLine }) {
  return <Badge>{zeroReasonLabel(line.zeroReason)}</Badge>
}

function Payouts({ statementId }: { statementId: string }) {
  const payouts = useAsync<PayoutSummary[]>(
    (signal) => api.get<PayoutSummary[]>(`/statements/${statementId}/payouts`, undefined, signal),
    [statementId],
  )

  // Not an error state. The route is optional to this screen: a statement nobody has paid
  // has nothing to show here, and a failure to list payouts must not blank the working
  // above it, which is the part somebody came to read.
  const rows = payouts.data ?? []
  if (rows.length === 0) return null

  return (
    <Card>
      <CardHeader title="Payouts" hint="A payout settles one statement in full and stores no amount of its own." />
      <Table>
        <thead>
          <tr>
            <Th>Paid</Th>
            <Th>Method</Th>
            <Th>Reference</Th>
            <Th>Account</Th>
            <Th numeric>Amount</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((payout) => (
            <Tr key={payout.id}>
              <Td>{date(payout.paidOn)}</Td>
              <Td>{payout.method ?? <span className="text-faint">&mdash;</span>}</Td>
              <Td className="font-mono text-[12px]">
                {payout.paymentReference ?? <span className="text-faint">&mdash;</span>}
              </Td>
              <Td className="tabular">
                {payout.bankAccountLastFour ? `•••• ${payout.bankAccountLastFour}` : '—'}
              </Td>
              <Td numeric className={payout.isReversal ? 'text-crit' : undefined}>
                {money(payout.amount, payout.currencyCode)}
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </Card>
  )
}

function RecordPayout({
  open,
  statementId,
  resellerId,
  amount,
  onClose,
  onDone,
}: {
  open: boolean
  statementId: string
  resellerId: string
  amount: string
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [paidOn, setPaidOn] = useState(isoDate())
  const [method, setMethod] = useState('Bank transfer')
  const [reference, setReference] = useState('')
  const [bankAccountId, setBankAccountId] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  const accounts = useAsync<{ id: string; bankName: string; lastFour: string; status: string }[]>(
    (signal) =>
      api.get<{ id: string; bankName: string; lastFour: string; status: string }[]>(
        `/resellers/${resellerId}/bank-accounts`,
        undefined,
        signal,
      ),
    [resellerId],
  )

  const active = (accounts.data ?? []).filter((account) => account.status === 'Active')

  async function record() {
    setSaving(true)
    try {
      await api.post(`/statements/${statementId}/payout`, {
        paidOn,
        method: method.trim() === '' ? null : method.trim(),
        paymentReference: reference.trim() === '' ? null : reference.trim(),
        bankAccountId: bankAccountId === '' ? null : bankAccountId,
        note: note.trim() === '' ? null : note.trim(),
      })
      toast.show('Payout recorded', 'success')
      onDone()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not record the payout', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="Record a payout"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={record} disabled={saving}>
            {saving ? 'Recording...' : 'Record payout'}
          </Button>
        </>
      }
    >
      <p className="mb-4 rounded-md border border-pos/30 bg-pos-wash px-4 py-3 text-[13px] text-pos">
        Settles the statement <b>in full</b>: <b className="tabular">{amount}</b>. There is no
        part-payment, because every line is commission on money the customer has already sent
        us &mdash; and the amount is the statement&rsquo;s, so there is nothing to type here.
      </p>

      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Date paid" required>
            {(id) => (
              <Input id={id} type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
            )}
          </Field>
          <Field label="Method">
            {(id) => <Input id={id} value={method} onChange={(e) => setMethod(e.target.value)} />}
          </Field>
        </div>
        <Field label="Into">
          {(id) => (
            <Select
              id={id}
              value={bankAccountId}
              onChange={(e) => setBankAccountId(e.target.value)}
            >
              <option value="">Not recorded</option>
              {active.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.bankName} •••• {account.lastFour}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label="Bank reference"
          hint="One transfer covering two statements is two payouts sharing this reference."
        >
          {(id) => <Input id={id} value={reference} onChange={(e) => setReference(e.target.value)} />}
        </Field>
        <Field label="Note">
          {(id) => <Textarea id={id} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />}
        </Field>
      </div>
    </Dialog>
  )
}
